import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeIn, staggerContainer } from './utils/animations';
import AnimateWhenVisible from './components/AnimateWhenVisible';
import { scaleUp } from './utils/animations';

const Services = () => {
  const services = [
    {
      id: 1,
      title: "Landing Page Development",
      description: "Conversion-focused landing pages designed to turn visitors into customers with compelling CTAs and optimized user journeys.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" />
        </svg>
      )
    },
    {
      id: 2,
      title: "SEO Optimization",
      description: "Improve your visibility in search results with on-page and technical SEO that helps your business rank higher and attract more visitors.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
        </svg>
      )
    },
    {
      id: 3,
      title: "Website Maintenance",
      description: "Keep your website secure, up-to-date, and running smoothly with regular maintenance and technical support.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
        </svg>
      )
    }
  ];

  // Add schema.org structured data for services
  useEffect(() => {
    const servicesSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": services.map((service, index) => ({
        "@type": "Service",
        "position": index + 1,
        "name": service.title,
        "description": service.description,
        "provider": {
          "@type": "ProfessionalService",
          "name": "forca.dev",
          "url": "https://forca.dev"
        },
        "url": `https://forca.dev/#services-${service.id}`
      }))
    };

    // Add the schema to the page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(servicesSchema);
    script.id = 'services-schema';
    
    // Remove any existing services schema to avoid duplicates
    const existingScript = document.getElementById('services-schema');
    if (existingScript) {
      existingScript.remove();
    }
    
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      const scriptToRemove = document.getElementById('services-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [services]);

  return (
    <motion.div 
      variants={staggerContainer(0.1, 0.2)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
      className="container mx-auto px-4 py-20"
      itemScope itemType="https://schema.org/ServiceCollection"
    >
      <motion.div 
        variants={fadeIn('up', 0.2)}
        className="relative mb-16 text-center"
      >
        <div className="absolute left-1/2 top-1/2 -z-10 h-36 w-36 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/0 blur-2xl" />
        <h2 className="relative text-4xl font-bold text-white md:text-5xl lg:text-6xl" itemProp="name">
          My <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">Services</span>
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-400" itemProp="description">
        Everything you need to build a strong and professional online presence.
        </p>
      </motion.div>

      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {services.map((service, index) => (
          <AnimateWhenVisible 
            key={service.id}
            variants={scaleUp}
            className="group relative"
            threshold={0.2}
          >
            <div 
              className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-slate-950 to-slate-900 p-[1px] shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-cyan-500/25"
              itemScope 
              itemType="https://schema.org/Service"
              id={`services-${service.id}`}
            >
              <meta itemProp="serviceType" content={service.title} />
              <div className="absolute inset-0 bg-gradient-to-b from-cyan-500 to-blue-500 opacity-10" />
              <div className="relative rounded-2xl bg-gradient-to-b from-slate-950 to-slate-900 p-8">
                <div className="absolute -left-16 -top-16 h-32 w-32 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/0 blur-2xl transition-all duration-500 group-hover:scale-150 group-hover:opacity-70" />
                <div className="absolute -bottom-16 -right-16 h-32 w-32 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/0 blur-2xl transition-all duration-500 group-hover:scale-150 group-hover:opacity-70" />
                
                <div className="relative mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-500">
                  {service.icon}
                </div>
                
                <h3 className="relative mb-4 text-xl font-bold text-white" itemProp="name">{service.title}</h3>
                <p className="relative text-slate-400" itemProp="description">{service.description}</p>
                
                <div className="relative mt-8">
                  <Link to="/contact" className="inline-flex items-center text-sm font-semibold text-cyan-500 transition-colors hover:text-cyan-400" itemProp="url">
                    Learn more
                    <svg stroke="currentColor" viewBox="0 0 24 24" fill="none" className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1">
                      <path d="M17 8l4 4m0 0l-4 4m4-4H3" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </AnimateWhenVisible>
        ))}
      </div>
    </motion.div>
  );
};

export default Services;
